import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRIO_COLORS = {
  "Crítica": "risk-5",
  "Alta": "risk-4",
  "Média": "risk-3",
  "Baixa": "risk-1",
};

export default function SuggestionsModal({ assessmentId, onClose, onApplied }) {
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/assessments/${assessmentId}/suggestions`)
      .then(({ data }) => {
        if (!mounted) return;
        setSuggestions(data.suggestions);
        const pre = {};
        data.suggestions.forEach((s) => {
          if (s.prioridade === "Crítica" || s.prioridade === "Alta") {
            pre[`${s.dimensao}::${s.acao}`] = true;
          }
        });
        setPicked(pre);
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [assessmentId]);

  const key = (s) => `${s.dimensao}::${s.acao}`;

  const selectAll = () => {
    const all = {};
    suggestions.forEach((s) => { all[key(s)] = true; });
    setPicked(all);
  };

  const applyChosen = async () => {
    const chosen = suggestions.filter((s) => picked[key(s)]);
    if (chosen.length === 0) {
      toast.info("Selecione ao menos uma sugestão.");
      return;
    }
    try {
      await Promise.all(chosen.map((s) =>
        api.post("/action-plans", {
          assessment_id: assessmentId,
          acao: s.acao,
          dimensao: s.dimensao,
          prioridade: s.prioridade,
          status: "Pendente",
          responsavel: "",
          prazo: "",
        })
      ));
      toast.success(`${chosen.length} ação(ões) adicionada(s) ao plano.`);
      onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const selectedCount = Object.values(picked).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white border w-full max-w-4xl max-h-[90vh] flex flex-col" data-testid="suggestions-modal">
        <div className="p-6 border-b border-slate-200 flex justify-between items-start">
          <div>
            <div className="overline mb-1">Sugestões automáticas</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Plano de ação inteligente</h2>
            <p className="text-sm text-slate-600 mt-1">
              Sugestões para todas as dimensões avaliadas (incluindo nível Baixo como ação preventiva). Selecione e adicione ao plano.
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="suggestions-close">
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <i className="fa-solid fa-circle-notch fa-spin text-2xl"></i>
              <p className="mt-3">Analisando resultados…</p>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <i className="fa-solid fa-circle-info text-3xl text-amber-600 mb-3"></i>
              <p>Sem respostas suficientes para gerar sugestões.</p>
              <p className="text-xs mt-2">Aguarde os colaboradores responderem o questionário e tente novamente.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2 mb-4 text-xs">
                <button onClick={selectAll} className="btn btn-secondary text-xs" data-testid="suggestions-select-all">
                  Selecionar todas
                </button>
                <button onClick={() => setPicked({})} className="btn btn-ghost text-xs" data-testid="suggestions-clear">
                  Limpar
                </button>
                <div className="ml-auto text-slate-500 self-center">
                  <span className="font-bold text-emerald-900">{selectedCount}</span> de {suggestions.length} selecionada(s)
                </div>
              </div>
              {suggestions.map((s) => {
                const k = key(s);
                const sel = !!picked[k];
                return (
                  <label key={k} className={`flex items-start gap-3 p-4 border cursor-pointer transition-all ${
                    sel ? "border-emerald-900 bg-emerald-50" : "border-slate-200 hover:border-slate-400 bg-white"
                  }`} data-testid={`suggestion-row-${k}`}>
                    <input type="checkbox" className="mt-1" checked={sel}
                           onChange={() => setPicked({ ...picked, [k]: !sel })}
                           data-testid={`suggestion-check-${k}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`${PRIO_COLORS[s.prioridade] || "risk-0"} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>
                          {s.prioridade}
                        </span>
                        <span className="text-xs text-slate-500 font-mono">Score {s.score} · {s.nivel_risco}</span>
                      </div>
                      <div className="font-semibold text-slate-900 text-sm">{s.acao}</div>
                      <div className="text-xs text-slate-500 mt-1">{s.dimensao}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary" data-testid="suggestions-cancel">Cancelar</button>
          <button onClick={applyChosen} disabled={suggestions.length === 0} className="btn btn-primary" data-testid="suggestions-apply">
            <i className="fa-solid fa-check"></i> Adicionar ao plano
          </button>
        </div>
      </div>
    </div>
  );
}
